import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import useFetchPortalDocument from '../../../../hooks/useApi/useFetchPortalDocument';
import PortalGuideForm from './PortalGuideForm';
import PortalGuideResult from './PortalGuideResult';
import styles from '../Video/VideoContainer.module.scss';

type FC = FunctionComponent;

const { Box, Title, Button, ViewPDFModal, Icon } = vp;

const pdfCategoryOrder = [
  'Getting Started',
  'My Account',
  'Contacts',
  'Transactions',
  'Bulk File Upload',
  'eLinx Payment Links',
  'VoPay Verify',
  'Transaction Approvals',
  'Client Accounts',
  'Reports',
  'Accounting Software Integrations',
  'Settings',
  'Payment Rails',
  'Partner Portal',
];

const PortalGuideContainer: FC = () => {
  const [documents, fetchPortalDocument] = useFetchPortalDocument({});
  const [activeTab, setActiveTab] = useState('');
  const [isDropdownSelected, setIsDropdownSelected] = useState(false);
  const [hasSearchResult, setHasSearchResult] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [filteredByCategories, setFilteredByCategories] = useState<{ [key: string]: vp.PortalDocument[] }>({});
  const [filteredByKeyWordLists, setFilteredByKeyWordLists] = useState<{ [key: string]: vp.PortalDocument }>({});
  const [selectedDocument, setSelectedDocument] = useState<vp.PortalDocument | null>(null);
  const [activeCategoryMenu, setActiveCategoryMenu] = useState<string | null>('Getting Started');

  useEffect(() => {
    fetchPortalDocument();
  }, []);

  useEffect(() => {
    if (!documents.Documents) return;

    if (Object.values(documents.Documents).length >= 1) {
      if (!documents.Categories) return;
      const categories = [...new Set(Object.values(documents.Categories).map((doc) => doc.Category))];

      const tabs = categories
        .filter((category) => !getDisabledTab(category, documents.Documents))
        .map((category) => ({
          label: category,
          isDisabled: getDisabledTab(category, documents.Documents),
          onClick: () => handleTabClick(category),
        }));

      setActiveTab(tabs[0].label);
    }
  }, [documents]);

  useEffect(() => {
    if (activeTab === '') return;

    if (!documents.Documents) return;

    const grouped = Object.values(documents.Documents).reduce<Record<string, vp.PortalDocument[]>>(
      (accumulator, doc) => {
        const category = doc.category?.toLowerCase();
        if (category && !accumulator[category]) {
          accumulator[category] = [];
        }
        if (category) {
          accumulator[category].push(doc);
        }

        return accumulator;
      },
      {},
    );

    const sortedFilteredByCategories = pdfCategoryOrder.reduce<Record<string, vp.PortalDocument[]>>(
      (acc, categoryName) => {
        const key = categoryName.toLowerCase();
        if (!grouped[key]) return acc;
        return {
          ...acc,
          [categoryName]: grouped[key],
        };
      },
      {},
    );

    setFilteredByCategories(sortedFilteredByCategories || {});
  }, [activeTab, selectedDocument, document]);

  useEffect(() => {
    if (!isDropdownSelected) {
      if (Object.values(documents.Documents).length < 1) return;
      if (!activeTab) return;

      const doc = Object.values(documents.Documents).find((d) =>
        d.category.toLowerCase().includes(activeTab.toLowerCase()),
      );

      if (doc) {
        setSelectedDocument(doc);
      }
    }
  }, [activeTab, documents]);

  useEffect(() => {
    if (!selectedDocument || !selectedDocument.category) return;
    setHasSearchResult(false);
  }, [selectedDocument]);

  useEffect(() => {
    if (searchTerm === '') setHasSearchResult(false);
  }, [searchTerm]);

  const getSelectedDocument = (docu: vp.PortalDocument) => {
    if (activeCategoryMenu !== docu.category) {
      handleMenuClick(docu.category);
    }
    setActiveTab(docu.category || '');
    setSelectedDocument(docu);
    setIsDropdownSelected(true);
    setHasSearchResult(false);
    setSearchTerm('');
  };

  const handleMenuClick = (menu: string) => {
    setActiveCategoryMenu((prev) => (prev === menu ? null : menu));
  };

  const PDFviewer = ({ docu }: { docu: vp.PortalDocument }) => (
    <>
      <h4>{docu.title}</h4>
      <div className={styles.PDFviewer}>
        <div className={styles.modalButton}>
          <Button
            id="open-pdf-modal"
            label=""
            background="transparent"
            width="18px"
            height="18px"
            size="xlg"
            icon="expand"
            color="none-light"
            iconLocation="center"
            isCircle
            onClick={() => {
              setIsModalVisible(!isModalVisible);
            }}
          />
        </div>
        <object
          data={`${docu.url}#toolbar=0&zoom=75`}
          type="application/pdf"
          style={{ width: '90%', height: '100%' }}
        />
      </div>
    </>
  );

  const MemoizedPDFviewer = useMemo(() => {
    if (selectedDocument) {
      return <PDFviewer docu={selectedDocument} />;
    }

    return null;
  }, [selectedDocument]);

  return (
    <Box flx="col">
      <ViewPDFModal
        isVisible={isModalVisible}
        pdfPath={selectedDocument?.url || ''}
        onDisagree={() => {
          setIsModalVisible(false);
        }}
      />
      <Box mb="md" jc="sb">
        <Title title="PDF Guides" />
      </Box>
      <Box flx="row" jc="sb" ai="center" mt="md" gap="md">
        <Box lineHeight="25px">
          Explore step-by-step guides designed to guide you through the VoPay Portal with ease.
        </Box>
        <PortalGuideForm
          documents={documents.Documents}
          filteredByKeyWordLists={filteredByKeyWordLists}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          setFilteredByKeyWordLists={setFilteredByKeyWordLists}
          setHasSearchResult={setHasSearchResult}
          getSelectedDocument={getSelectedDocument}
        />
      </Box>
      {Object.entries(filteredByCategories).length >= 1 && (
        <div className={styles.guideContainer}>
          <div className={[styles.guideListsContainer, styles.pdfListContainer].join(' ')}>
            <div className={styles.guideScrollableList}>
              {Object.entries(filteredByCategories).map(([category, docs]) => (
                <div key={category} className={styles.categoryContainer}>
                  <div
                    className={`${styles.categoryTitle} ${
                      activeCategoryMenu === category ? styles.activeCategoryMenu : ''
                    }`}
                    onClick={() => {
                      handleMenuClick(category);
                      setSelectedDocument(docs[0]);
                    }}
                  >
                    <h4>{category}</h4>
                    {activeCategoryMenu === category ? (
                      <Icon icon="angleTop" size="xsm" />
                    ) : (
                      <Icon icon="angleDown" size="xsm" />
                    )}
                  </div>
                  {activeCategoryMenu === category && (
                    <div className={styles.documentsList}>
                      {docs
                        .sort((a, b) => pdfCategoryOrder.indexOf(a.title) - pdfCategoryOrder.indexOf(b.title))
                        .map((doc) => (
                          <div
                            key={doc.title}
                            className={[styles.guideList, styles[getActiveClass(doc.title)]].join(' ')}
                            onClick={() => setSelectedDocument(doc)}
                          >
                            <h5 className={styles.details}>
                              {doc.title === selectedDocument?.title ? <span></span> : null}
                              {doc.title}
                            </h5>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {hasSearchResult ? (
            <PortalGuideResult
              searchTerm={searchTerm}
              filteredByKeyWordLists={filteredByKeyWordLists}
              getSelectedDocument={getSelectedDocument}
            />
          ) : (
            <div className={styles.guide}>{selectedDocument?.title !== '' && MemoizedPDFviewer}</div>
          )}
        </div>
      )}
    </Box>
  );

  function getActiveClass(title: string) {
    if (!selectedDocument) return '';

    return title === selectedDocument.title && !hasSearchResult ? 'active' : '';
  }

  function handleTabClick(tab: string) {
    setActiveTab(tab);
    setIsDropdownSelected(false);
    setHasSearchResult(false);
    setSearchTerm('');
  }

  function getDisabledTab(label: string, docs: { [key: number]: vp.PortalDocument }) {
    return !Object.values(docs).some((docu) => docu.category === label);
  }
};

export default PortalGuideContainer;
